import { displayUnitOf } from "@heraion/shared";
import { useCallback, useMemo, useState } from "react";
import { ScrollView } from "react-native";
import { useTranslation } from "react-i18next";
import {
  programWeeklyPrescription,
  type BodyFigure,
  type PlanStarter,
  type Program,
} from "@heraion/shared";
import {
  ProgressRecordsCard,
  ProgressView,
  resolveTheme,
  useCatalogItems,
  useSessionsSummary,
  useTagVolumeRecords,
  usePrograms,
} from "@heraion/ui";
import { apiFetch } from "./api";
import { exerciseCatalog } from "./logger/exercisesCache";
import { notifyViaWindow } from "./confirm";
import { scheduleIdle } from "./progress/scheduleIdle";
import { shareOrDownloadPngs } from "./logger/shareCardCaptureWeb";
import { durableWrite } from "./durableWrite";
import { useProgressLayoutProps } from "./useHomeLayoutProps";
import { useProfileDoc } from "./useProfileDoc";
import { useSelfProgressDeps } from "./progress/progressDeps";
import { useSelfAthleteTargets } from "./progress/useSelfAthleteTargets";
import { useSelfCoverageDeps } from "./progress/useSelfCoverageDeps";
import { useThemePalette } from "./theme";
import { useWorkoutStarter } from "./useWorkoutStarter";

// THE Progress surface on web-rnw — the SAME shared ProgressView mobile's
// Progress tab mounts (the coverage census + supporting panels + Tier-2 metrics +
// session stats + frequency chart). The panels self-fetch through apiFetch.
// The volume-by-tag records card is now the SAME shared card mobile mounts
// (lift/progress-tag-volume-web, which unblocked RW-8). painMap comes from
// useSelfProgressDeps (BodyScreen's PainMapPicker writes it, lifted
// 2026-08-16): the pain panel + alert escalation now see real data. See
// next/rnw-strangler.md.

const formatDate = (iso: string, locale: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
  }).format(d);
};

export function ProgressScreen({
  onStartWorkout,
}: {
  // AP-9b: the coverage card's «Build today's session» hands its starter to
  // AppShell, which opens the logger (the Plan surface's seam).
  onStartWorkout?: (starter?: PlanStarter) => void;
} = {}) {
  const { dark, family } = useThemePalette();
  const { t, i18n } = useTranslation();
  const theme = resolveTheme(dark, family);
  const { doc, savePatch } = useProfileDoc();
  const weekStartDay = doc?.["weekStartDay"] as number | undefined;
  const { summary, weeklyCounts, weekStreak, error } = useSessionsSummary(
    apiFetch,
    weekStartDay,
  );
  const layoutProps = useProgressLayoutProps(doc, savePatch);
  // Catalog (slug → muscle) for the coverage hero's programWeeklyPrescription —
  // the shared picker hook, straight to network here (no cache passed) like the
  // rest of web Progress. Was a bespoke useProgressBodyMapData before the
  // bodymap panel was retired (§13.1); we reuse the primitive rather than
  // re-mint a one-off catalog fetch.
  const { raw: catalog } = useCatalogItems({ catalog: exerciseCatalog });
  const figure = doc?.["preferredFigure"] as BodyFigure | undefined;

  // PG-1 muscle coverage — the pinned "active block" + its per-muscle weekly
  // prescription, so the coverage hero scores against real targets instead of
  // degrading to neutral bars. Mirrors the mobile ProgressScreen wiring: the
  // followed programs resolve currentProgramId → the pinned Program, and the
  // full catalog (useProgressCatalog) feeds programWeeklyPrescription.
  const currentProgramId = doc?.["currentProgramId"] as string | undefined;
  const programsState = usePrograms({ apiFetch, durableWrite });
  const pinnedProgram = useMemo<Program | null>(
    () =>
      currentProgramId
        ? (programsState.followedPrograms.find(
            (p) => p.id === currentProgramId,
          ) ?? null)
        : null,
    [programsState.followedPrograms, currentProgramId],
  );
  const prescription = useMemo(
    () =>
      pinnedProgram && catalog
        ? programWeeklyPrescription(pinnedProgram, catalog)
        : null,
    [pinnedProgram, catalog],
  );

  // The shared self base (apiFetch, theme, units, resultsCache, isDark,
  // weekStartDay) + the PG-1 extras this surface computes on top.
  const base = useSelfProgressDeps();
  const rawBodyweight = doc?.["bodyweightKg"];
  const bodyweightKg = typeof rawBodyweight === "number" ? rawBodyweight : null;
  const toLogger = useCallback(
    (starter: PlanStarter) => onStartWorkout?.(starter),
    [onStartWorkout],
  );
  const { startBuiltSession } = useWorkoutStarter(null, bodyweightKg, toLogger);
  // AP-9b (Antenore, option a): the athlete's own targets, as mobile's
  // useProgressDeps passes them. Without them the coverage always ran
  // degraded on web, so the below-MEV sentence and its link never showed.
  const { value: athleteTargets } = useSelfAthleteTargets();
  const deps = useSelfCoverageDeps(base, {
    figure,
    pinnedProgram,
    prescription,
    athleteTargets,
    startBuiltSession: onStartWorkout ? startBuiltSession : undefined,
  });

  // Volume-by-tag records card — the SAME shared card + data hook mobile
  // Progress mounts (lift/progress-tag-volume-web). Web has no offline
  // cache to protect, but the fetch still competes with everything else on
  // this mount, so it's deferred the browser way (scheduleIdle) instead of
  // mobile's InteractionManager.
  // PG-39: only start the fetch once the card is actually shown, its
  // widget on and open (PG-57).
  const [recordsOpened, setRecordsOpened] = useState(false);
  const { chartError, tagWeeks } = useTagVolumeRecords({
    apiFetch,
    catalog,
    weekStartDay,
    enabled: recordsOpened,
    schedule: scheduleIdle,
  });
  const displayUnit = displayUnitOf(doc);

  // PG-64: the report's pages to the Web Share API, else a download each,
  // numbered in page order.
  const shareReportImage = useCallback(
    (uris: string[], title: string, fileStem: string) => {
      const stem = `${t("brand.product").toLowerCase()}-${fileStem}`;
      return shareOrDownloadPngs(
        uris,
        uris.map((_, i) => (uris.length > 1 ? `${stem}-${i + 1}.png` : `${stem}.png`)),
        title,
      );
    },
    [t],
  );
  const onShareReportError = useCallback(
    (msg: string) =>
      notifyViaWindow(
        t("mobile.postSession.shareErrorTitle", { defaultValue: "Share failed" }),
        msg,
      ),
    [t],
  );

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.palette.bg }}
      contentContainerStyle={{ padding: 16, gap: 16 }}
    >
      <ProgressView
        deps={deps}
        summary={summary}
        weeklyCounts={weeklyCounts}
        weekStreak={weekStreak}
        summaryError={error}
        formatDate={formatDate}
        locale={i18n.language}
        {...layoutProps}
        onShareReportImage={shareReportImage}
        onShareReportError={onShareReportError}
        recordsSlot={
          <ProgressRecordsCard
            tagWeeks={tagWeeks}
            chartError={chartError}
            displayUnit={displayUnit}
            locale={i18n.language}
            theme={theme}
            onExpand={() => setRecordsOpened(true)}
          />
        }
      />
    </ScrollView>
  );
}
